import {
  BaselinePersonalizationItemState,
  useGetBaselinePersonalizationItemsQuery,
  RtkqSpin,
  BaselineSpecialRequestState,
  useGetBaselineSpecialRequestsQuery,
  BaselineExistingItemState,
  useGetBaselineExistingItemsQuery,
} from '@/redux';
import { chain } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaselinePersonalizationTableRow } from './BaselinePersonalizationTablePanel';
import { array } from '@/utils';

export function useBaselinePersonalizationData(props: {
  homeId: string;
  projectIds?: string[] | null;
}) {
  const { homeId, projectIds } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.summary',
  });

  const { data = array.empty<BaselinePersonalizationItemState>(), isLoading } =
    useGetBaselinePersonalizationItemsQuery({
      homeId,
      projectIds: projectIds ?? undefined,
      [RtkqSpin]: false,
    });

  const { data: specialRequests = array.empty<BaselineSpecialRequestState>() } =
    useGetBaselineSpecialRequestsQuery({
      projectIds: projectIds ?? undefined,
      homeId,
      [RtkqSpin]: false,
    });

  const { data: existingItems = array.empty<BaselineExistingItemState>() } =
    useGetBaselineExistingItemsQuery({
      projectIds: projectIds ?? undefined,
      homeId,
      [RtkqSpin]: false,
    });

  const specialRequestRowValues = useMemo<BaselinePersonalizationTableRow[]>(
    () =>
      specialRequests.map((x) => ({
        name: x.name,
        room: x.room,
        isFloorplan: x.isFloorplan,
        projectName: x.project.name,
        createdAt: x.createdAt,
        description: x.description,
        categoryPath: x.category.path,
        specialRequest: x,
        number: x.number,
      })),
    [specialRequests],
  );

  const personalizationItemRowValues = useMemo<
    BaselinePersonalizationTableRow[]
  >(
    () =>
      data.map((x) => ({
        name: x.name,
        number: x.number,
        room: x.room,
        isFloorplan: x.isFloorplan,
        projectName: x.project.name,
        createdAt: x.createdAt,
        description: x.description,
        categoryPath: x.category.path,
        personalizationItem: x,
      })),
    [data],
  );

  const existingItemRowValues = useMemo<BaselinePersonalizationTableRow[]>(
    () =>
      existingItems.map((x) => ({
        name: t('existingItem', { name: x.name }),
        room: x.room,
        isFloorplan: false,
        description: '',
        categoryPath: x.category.path,
        isExistingItem: true,
        projectName: x.project.name,
        createdAt: x.createdAt,
        number: null,
      })),
    [existingItems, t],
  );

  const values: BaselinePersonalizationTableRow[] = [
    ...personalizationItemRowValues,
    ...existingItemRowValues,
    ...specialRequestRowValues.filter(
      (x) =>
        x.specialRequest?.status !== 'Denied' &&
        x.specialRequest?.status !== 'ResidentDeclined',
    ),
  ];

  const floorplanData = values.filter((x) => x.isFloorplan);

  const wholeHomeData = values.filter((x) => !x.isFloorplan && !x.room);

  const roomsData = chain(values)
    .filter((x) => !x.isFloorplan && !!x.room)
    .groupBy((x) => x.room?.id!)
    .map((value) => ({
      roomId: value.at(0)!.room?.id,
      roomName: value.at(0)!.room?.name,
      roomCreatedAt: value.at(0)!.room?.createdAt,
      value,
    }))
    .sort((a, b) =>
      (a.roomCreatedAt || '').localeCompare(b.roomCreatedAt ?? ''),
    )
    .value();

  return {
    loading: isLoading,
    totalHomeData: values,
    floorplanData,
    wholeHomeData,
    roomsData,
    allPersonalizations: values,
    inactiveData: specialRequestRowValues.filter(
      (x) =>
        x.specialRequest?.status === 'Denied' ||
        x.specialRequest?.status === 'ResidentDeclined',
    ),
  };
}
